import { useRbacEnabled } from '@/hooks/useRbacEnabled';
import { useWhoami } from '@/hooks/useWhoami';
interface Entitlements {
  canRead: boolean;
  canWriteAssets: boolean;
  canWriteJobs: boolean;
  canWriteFiles: boolean;
  canConverse: boolean;
  canManageAccounts: boolean;
  canManageSettings: boolean;
  canManageIntegrations: boolean;
  isPraetorian: boolean;
  isSuperAdmin: boolean;
  isLoading: boolean;
}

/**
 * Returns a boolean for each entitlement the current user may hold.
 *
 * When the Statsig `enable_rbac` gate is off, every property is `true`
 * (transparent / no enforcement) EXCEPT `isSuperAdmin`, which always
 * reflects the server-granted entitlement. Super-admin enforcement on the
 * backend is unconditional (not Statsig-gated), so this property must
 * converge to the server-side truth regardless of the gate; it is
 * fail-closed while `/whoami` loads.
 *
 * @example
 * ```tsx
 * const { canWriteAssets, canManageSettings } = useEntitlements();
 *
 * if (canWriteAssets) {
 *   deleteSelectedItems();
 * }
 * ```
 */
export function useEntitlements(): Entitlements {
  const rbacEnabled = useRbacEnabled();
  const { hasEntitlement, isLoading } = useWhoami();

  if (!rbacEnabled) {
    return {
      canRead: true,
      canWriteAssets: true,
      canWriteJobs: true,
      canWriteFiles: true,
      canConverse: true,
      canManageAccounts: true,
      canManageSettings: true,
      canManageIntegrations: true,
      isPraetorian: true,
      isSuperAdmin: hasEntitlement('super_admin'),
      isLoading: false,
    };
  }

  return {
    canRead: hasEntitlement('read'),
    canWriteAssets: hasEntitlement('write_assets'),
    canWriteJobs: hasEntitlement('write_jobs'),
    canWriteFiles: hasEntitlement('write_files'),
    canConverse: hasEntitlement('conversation_ai'),
    canManageAccounts: hasEntitlement('manage_accounts'),
    canManageSettings: hasEntitlement('manage_settings'),
    canManageIntegrations: hasEntitlement('manage_integrations'),
    isPraetorian: hasEntitlement('praetorian'),
    isSuperAdmin: hasEntitlement('super_admin'),
    isLoading,
  };
}
